import { FC, memo, ReactNode, useCallback } from "react";
// i18next
import { useTranslation } from "react-i18next";
// Hooks
import useFormattedDate from "app/hooks/useFormattedDate";
import useFormattedTime from "app/hooks/useFormattedTime";
// Redux
import { useAppDispatch, useAppSelector } from "app/hooks/useStore";
// Models
import { RootState } from "app/store";
import IEpisode from "app/models/Episode";
// Actions
import { EpisodesActions } from "app/store/Episodes/Episodes.slice";
import { AppUIActions } from "app/store/AppUI/AppUI.slice";
import PageActions from "app/store/page/page.actions";
import { AppUiDialogActions } from "app/store/AppUIDialog/AppUIDialog.slice";
// Selectors
import { selectEpisodesFilters, selectSelectedEpisodeId } from "app/store/Episodes/Episodes.selectors";
import { selectIsPageInCompare, selectPageEntity, selectPrimaryPage } from "app/store/page/page.selectors";
import { selectPreviewPage } from "app/store/AppUI/AppUI.selectors";
// MUI
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import {
  VisibilityOutlined as VisibilityOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
  StarBorder as StarBorderIcon
} from '@mui/icons-material';
// Components
import Icon from "app/components/Icon";

type Props = {
  episode: IEpisode;
}

const EpisodesListItem:FC<Props> = ({ episode }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  
  const filters = useAppSelector(selectEpisodesFilters);
  const page = useAppSelector((state:RootState) => selectPageEntity(state, { documentId: episode.documentId, pageNum: episode.pageNum }));
  const primaryPage = useAppSelector((state:RootState) => selectPrimaryPage(state, { documentId: episode.documentId, pageNum: episode.pageNum }));

  const previewPage = useAppSelector(selectPreviewPage);
  const isPreviewPageOpen = previewPage && previewPage.documentId === episode.documentId && previewPage.pageNum === episode.pageNum;

  const stapleId = page?.staple && page.staple.order === 1 ? page.staple.id : null;

  const selectedEpisodeId = useAppSelector(selectSelectedEpisodeId);
  const isPageInCompare = useAppSelector((state:any) => selectIsPageInCompare(state, {
    documentId: episode.documentId,
    pageNum: episode.pageNum,
    stapleId: null
  }));

  const formattedDate = useFormattedDate(episode.date);
  const formattedTime = useFormattedTime(episode.time);
    
  const onClick = useCallback((episodeId: number) => {
    const nextEpisodeId = episodeId === selectedEpisodeId ? null : episodeId;
    dispatch(EpisodesActions.setSelectedEpisodeId(nextEpisodeId));
    // eslint-disable-next-line
  }, [selectedEpisodeId])

  const openPagePreview = useCallback((episode:IEpisode) => {
    if (isPreviewPageOpen) {
      dispatch(AppUIActions.setPreviewPageOpen(false));
      dispatch(AppUIActions.setPreviewPage(null));
    } else {
      dispatch(AppUIActions.setPreviewPageOpen(true));
      dispatch(AppUIActions.setPreviewPage({
        documentId: episode.documentId,
        pageNum: episode.pageNum
      }));
    }
    // eslint-disable-next-line
  }, [isPreviewPageOpen])
  
  const handleTogglePageCompare = () => {
    if ( isPageInCompare ){
      dispatch(PageActions.removePageFromCompare(
        episode.documentId,
        episode.pageNum,
        stapleId
      ));
    } else {
      dispatch(PageActions.addPageToCompare(
        episode.documentId,
        episode.pageNum,
        stapleId
      ));
    }
  }

  const handleClickEdit = () => {
    if (!page || !primaryPage) return;
    dispatch(AppUiDialogActions.showDialog({
      dialogName: 'PageDetailsDialog',
      dialogProps: {
        documentId: page.documentId,
        pageNum: primaryPage.pageNum,
        openedPageNum: page.pageNum,
        showNavigations: false,
        activeEpisodeId: episode.id,
      }
    }));
  }

  const isSelected = selectedEpisodeId === episode.id;
  const isParentEpisode = primaryPage?.parentEpisodeId && primaryPage.parentEpisodeId === episode.id;
  
  const TextBlock:FC<{width:number;children:ReactNode}> = ({ width, children }) => (
    <Box sx={{ width: `${width}px` }}>
      {typeof children === 'string' ? (
        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{children}</Typography>
      ) : children}
    </Box>
  )

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      py: 1.5, px: 4,
    }}>
      <TextBlock width={60}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ minWidth: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {isParentEpisode ? (
              <Tooltip title={t('labels.parentEpisode')}>
                <StarBorderIcon sx={{ color: 'rgb(102, 102, 102)' }} />
              </Tooltip>
            ) : null}
          </Box>
          <Box sx={{ minWidth: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {page?.inWorkspace && filters.inWorkspace === 'all' ? (
              <Tooltip title={t('labels.pageInWorkspace')}>
                <span>
                  <Icon icon="add_to_workspace" sx={{ color: '#666' }} />
                </span>
              </Tooltip>
            ) : null}
          </Box>
        </Box>
      </TextBlock>
      <TextBlock width={180}>
        <span>{formattedDate}</span>
        <Typography component="span" sx={{ display: 'inline-block', pl: 1 }}>{formattedTime}</Typography>
      </TextBlock>
      <TextBlock width={260}>{episode.type || ''}</TextBlock>
      <TextBlock width={260}>{episode.author?.name || ''}</TextBlock>
      {/* <TextBlock width={150}>
        {page?.tag ? <Chip size="small" label={page?.tag} /> : null}
      </TextBlock> */}
      <TextBlock width={240}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {episode.labels?.map((label: string) => (
            <Chip key={label} size="small" label={label} sx={{ borderRadius: '4px' }} />
          ))}
        </Box>
      </TextBlock>
      <TextBlock width={70}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {page?.colors?.map((color:string) => (
            <Box key={color} className={`bg-${color}`} sx={{ width: '12px', height: '12px', borderRadius: '50%' }} />
          ))}
        </Box>
      </TextBlock>
      <TextBlock width={132}>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
          {episode.notes ? (
            <Tooltip title={t('pages.episodes.tooltip.notes')}>
              <IconButton size="small" onClick={() => onClick(episode.id)} color={isSelected ? 'primary' : 'default'}>
                <Icon icon="notes" size="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          <Tooltip title={isPageInCompare ? t('buttons.removePageFromCompare') : t('buttons.addPageToCompare')}>
            <IconButton
              name={isPageInCompare ? 'Remove page from compare' : 'Add page to compare'}
              aria-label={isPageInCompare ? 'Remove page from compare' : 'Add page to compare'}
              onClick={handleTogglePageCompare}
              color={isPageInCompare ? 'primary' : 'default'}
              size="small"
            ><Icon icon="compare" /></IconButton>
          </Tooltip>
          <Tooltip title={t('pages.episodes.tooltip.pagePreview')} color={isPreviewPageOpen ? 'primary' : 'default'}>
            <IconButton size="small" onClick={() => openPagePreview(episode)}>
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('buttons.edit')}>
            <IconButton
              size="small"
              onClick={handleClickEdit}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TextBlock>
    </Box>
  )
}

export default memo(EpisodesListItem);
